@file:Suppress("LongParameterList")

package floris.graphics.backoffice.components

import androidx.compose.runtime.Composable
import com.materialdesignsystem.components.widgets.DsSpinner
import com.materialdesignsystem.components.widgets.DsVideo
import com.materialdesignsystem.components.widgets.SpinnerSize
import com.materialdesignsystem.extensions.fileChooser
import com.materialdesignsystem.toColorScheme
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignContent
import com.varabyte.kobweb.compose.ui.modifiers.background
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.objectFit
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.right
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.top
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.fa.FaCamera
import com.varabyte.kobweb.silk.components.icons.fa.FaPencil
import com.varabyte.kobweb.silk.components.icons.fa.FaTrashCan
import com.varabyte.kobweb.silk.components.icons.fa.FaVideo
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.document
import org.jetbrains.compose.web.css.AlignContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Text
import org.w3c.files.File

private const val END_NEGATIVE_PADDING = (-12)
private const val TOP_NEGATIVE_PADDING = (-24)

@Composable
fun VideoChooser(
    modifier: Modifier = Modifier,
    id: String,
    label: String,
    videoUrl: String? = null,
    horizontalAlignment: Alignment.Horizontal = Alignment.CenterHorizontally,
    centerText: String = "",
    isLoading: Boolean,
    onFileSelected: (File) -> Unit,
    onDeleteClicked: (() -> Unit)? = null
) {
    FileChooserWithPreview(
        modifier = modifier,
        id = id,
        label = label,
        accept = "video/*",
        fileUrl = videoUrl,
        horizontalAlignment = horizontalAlignment,
        centerText = centerText,
        isLoading = isLoading,
        previewContent = {
            DsVideo(
                videoUrl = videoUrl ?: "",
                modifier = Modifier
                    .fillMaxSize()
                    .borderRadius(14.px)
                    .objectFit(ObjectFit.Contain),
                controls = true
            )
        },
        onFileSelected = onFileSelected,
        onDeleteClicked = onDeleteClicked
    )
}

@Composable
fun ImageChooser(
    modifier: Modifier = Modifier,
    id: String,
    label: String,
    imageUrl: String? = null,
    horizontalAlignment: Alignment.Horizontal = Alignment.CenterHorizontally,
    previewRoundedCorners: Boolean = false,
    centerText: String = "",
    isLoading: Boolean,
    onFileSelected: (File) -> Unit,
    onDeleteClicked: (() -> Unit)? = null
) {
    FileChooserWithPreview(
        modifier = modifier,
        id = id,
        label = label,
        accept = "image/*",
        fileUrl = imageUrl,
        horizontalAlignment = horizontalAlignment,
        centerText = centerText,
        isLoading = isLoading,
        previewContent = {
            Image(
                src = imageUrl ?: "",
                modifier = Modifier
                    .fillMaxSize()
                    .objectFit(ObjectFit.Contain)
                    .thenIf(previewRoundedCorners) {
                        Modifier.borderRadius(14.px)
                    }
            )
        },
        onFileSelected = onFileSelected,
        onDeleteClicked = onDeleteClicked
    )
}

@Composable
fun FileChooserWithPreview(
    modifier: Modifier = Modifier,
    id: String,
    label: String,
    accept: String,
    horizontalAlignment: Alignment.Horizontal,
    fileUrl: String? = null,
    centerText: String = "",
    isLoading: Boolean = false,
    previewContent: @Composable () -> Unit,
    onFileSelected: (File) -> Unit,
    onDeleteClicked: (() -> Unit)? = null
) {
    val colorScheme = ColorMode.current.toColorScheme

    fun fileChooser() = if (!isLoading) document.fileChooser(
        accept = accept,
        multiple = false,
        onFileSelected = { onFileSelected(it.first()) }
    ) else Unit

    Column(
        modifier = Modifier.fillMaxWidth(),
        horizontalAlignment = horizontalAlignment
    ) {
        Label(
            attrs = Modifier
                .margin(bottom = 0.5.cssRem)
                .toAttrs(),
            forId = id
        ) {
            Text(value = label)
        }

        Box(
            modifier = modifier
                .id(id)
                .border(2.px, LineStyle.Dashed, colorScheme.outline.toRgb().copyf(alpha = 0.5f))
                .borderRadius(14.px)
                .thenIf(fileUrl != null) {
                    Modifier.padding(12.px)
                }
                .position(Position.Relative),
            contentAlignment = Alignment.Center
        ) {
            if (isLoading) {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center
                ) {
                    DsSpinner(size = SpinnerSize.Large)
                }

                return@Box
            }

            if (fileUrl == null) {
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(16.px)
                        .onClick { fileChooser() }
                        .cursor(Cursor.Pointer),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(8.px, Alignment.CenterVertically)
                ) {
                    when {
                        accept.startsWith("image") -> FaCamera(
                            modifier = Modifier.height(16.px),
                            size = IconSize.XXL
                        )

                        accept.startsWith("video") -> FaVideo(
                            modifier = Modifier.height(16.px),
                            size = IconSize.XXL
                        )

                        else -> Unit // Handle other file types if needed
                    }

                    SpanText(
                        text = centerText,
                        modifier = Modifier
                            .textAlign(TextAlign.Center)
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            if (fileUrl != null) {
                // TODO [TicketNumber]: When the dimensions are different than 16:9, the image will stretch the parent's box as well..
                previewContent()

                Row(
                    modifier = Modifier
                        .position(Position.Absolute)
                        .top(TOP_NEGATIVE_PADDING.px)
                        .right(END_NEGATIVE_PADDING.px),
                    horizontalArrangement = Arrangement.spacedBy(4.px, Alignment.CenterHorizontally),
                ) {
                    if (onDeleteClicked != null) {
                        FaTrashCan(
                            modifier = Modifier
                                .size(40.px)
                                .borderRadius(100.percent)
                                .cursor(Cursor.Pointer)
                                .background(colorScheme.error)
                                .color(colorScheme.onError)
                                .onClick { onDeleteClicked() }
                                .textAlign(TextAlign.Center)
                                .alignContent(AlignContent.Center),
                            size = IconSize.LG
                        )
                    }

                    FaPencil(
                        modifier = Modifier
                            .size(40.px)
                            .borderRadius(100.percent)
                            .border(2.px, LineStyle.Solid, colorScheme.outline.toRgb().copyf(alpha = 0.5f))
                            .cursor(Cursor.Pointer)
                            .background(colorScheme.background)
                            .onClick { fileChooser() }
                            .textAlign(TextAlign.Center)
                            .alignContent(AlignContent.Center),
                        size = IconSize.LG
                    )
                }
            }
        }
    }
}
